'use client';

import React, { useId, useState } from 'react';
import { useController, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import clsx from 'clsx';
import * as yup from 'yup';
import { useTranslations } from 'next-intl';
import { usePathname } from 'next/navigation';
import {
  BooleanFragment,
  RichTextFragment,
  SingleLineFragment,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { TrackingConversionType } from '@/utils/tracking-conversion-types';
import { DataLayerHelper } from '@/utils/data-layer-helper';
import { buildTrackingId } from '@/utils/tracking-helper';
import { useLocalization } from '@/ui/providers/localizationProvider';
import { useOrderReference } from '@/ui/providers/order-reference-provider';
import { useCallOnce } from '@/ui/hooks/use-call-once';
import Container from '@/ui/components/shared/Container';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import TextInput from '@/ui/components/shared/inputs/TextInput';
import Button from '@/ui/components/shared/Button';
import { submitStarRating } from './star-rating-actions';
import { StarRatingStars } from './star-rating-stars';
import {
  STAR_RATING_MAX_COMMENT_LENGTH,
  STAR_RATING_MAX_STARS,
  StarRatingFeedbackEvent,
  StarRatingVariant,
  isValidTrackingLocation,
} from './star-rating-types';

export type StarRatingTile = {
  title: SingleLineFragment;
  text?: RichTextFragment;
  'show-order-id'?: BooleanFragment;
  'show-comment-field'?: BooleanFragment;
  'comment-placeholder'?: SingleLineFragment;
  'submit-button-text': SingleLineFragment;
  'confirmation-title': SingleLineFragment;
  'confirmation-text'?: RichTextFragment;
  'tracking-url': SingleLineFragment;
};

interface StarRatingProps {
  tile: StarRatingTile;
  trackingPrefix?: string;
  /** Renders a top divider to separate the tile when it is stacked below others (order page). */
  hasBorderTop?: boolean;
}

interface FormData {
  rating: number;
  comment?: string;
}

export function StarRating({ tile, trackingPrefix, hasBorderTop }: Readonly<StarRatingProps>) {
  const t = useTranslations('StarRating');
  const pathname = usePathname();
  const { country } = useLocalization();
  const orderReference = useOrderReference()?.orderReference;

  // Scopes every element id and the radio group name to this tile instance, so a page rendering the
  // tile twice cannot end up with colliding ids or a shared radio group.
  const uid = useId();

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submissionError, setSubmissionError] = useState('');

  const location = tile['tracking-url']?.text?.trim() ?? '';
  const showCommentField = tile['show-comment-field']?.value ?? false;
  const showOrderId = tile['show-order-id']?.value ?? false;
  const variant: StarRatingVariant = showCommentField ? 'rating_comment' : 'rating_only';

  // When stacked below other tiles (order page), a divider separates it. The padding below the line
  // sits just under the column's space-y gap above it to offset the heading's line-height leading, so
  // the line reads as optically centred between the two.
  const containerClassName = clsx(
    'my-0 overflow-hidden px-0 sm:my-0 sm:px-0 lg:my-0',
    hasBorderTop && 'border-t border-neutral-15 pt-3 sm:pt-5',
  );

  const validationSchema = yup.object({
    rating: yup
      .number()
      .integer(t('formRequiredRating'))
      .min(1, t('formRequiredRating'))
      .max(STAR_RATING_MAX_STARS, t('formRequiredRating'))
      .required(t('formRequiredRating')),
    comment: yup.string().max(STAR_RATING_MAX_COMMENT_LENGTH, t('formMaxLengthExceeded')),
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      rating: 0,
      comment: '',
    },
  });

  // The star group is driven through a controller rather than `register` so its DOM `name` can be
  // made instance-unique: react-hook-form resolves the field from `event.target.name`, so
  // overriding the name on a registered input would silently drop the selected value.
  const { field: ratingField } = useController({ name: 'rating', control });

  const pushFeedbackEventOnce = useCallOnce((feedback: StarRatingFeedbackEvent) => {
    DataLayerHelper.pushConversionEvent(TrackingConversionType.FEEDBACK_SUBMIT, { feedback });
  });

  const onSubmit = async (data: FormData) => {
    setSubmissionError('');

    const comment = showCommentField ? data.comment?.trim() : undefined;

    const result = await submitStarRating({
      rating: data.rating,
      variant,
      location,
      url: pathname,
      countryCode: country.toUpperCase(),
      ...(comment ? { comment } : {}),
      ...(orderReference ? { orderReference } : {}),
    });

    if (!result.success) {
      setSubmissionError(t('formErrorSubmissionFailed'));
      return;
    }

    pushFeedbackEventOnce({
      rating: data.rating,
      ...(variant === 'rating_comment' ? { comment_provided: Boolean(comment) } : {}),
      variant,
      location,
    });

    setIsSubmitted(true);
  };

  // A tile without a valid snake_case tracking location would produce an unusable or unmatchable
  // `feedback_` survey id, so it is treated as a misconfiguration and rendered as nothing at all.
  if (!isValidTrackingLocation(location)) {
    return null;
  }

  if (isSubmitted) {
    const confirmationText = tile['confirmation-text'];

    return (
      <Container className={containerClassName}>
        <div className="max-w-3xl">
          <h2 className="mb-2 text-xl font-medium leading-tight">
            {tile['confirmation-title']?.text ?? t('confirmationTitle')}
          </h2>
          {confirmationText?.json?.length ? (
            <RichTextRenderer blocks={confirmationText} className="text-base text-neutral-50" />
          ) : (
            <p className="text-base text-neutral-50">{t('confirmationText')}</p>
          )}
        </div>
      </Container>
    );
  }

  const selectedRating = Number(ratingField.value) || 0;

  return (
    <Container className={containerClassName}>
      <div className="max-w-3xl">
        <div className="mb-6">
          <h2 className="mb-2 text-xl font-medium leading-tight">
            {tile.title?.text ?? t('title')}
          </h2>
          {tile.text?.json?.length ? (
            <RichTextRenderer blocks={tile.text} className="text-base" />
          ) : null}
          {showOrderId && orderReference && (
            <p className="mt-1 text-sm text-neutral-50 sm:text-base">
              {t('orderLabel', { orderReference })}
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
          <StarRatingStars
            idPrefix={uid}
            name={`${uid}-rating`}
            selectedRating={selectedRating}
            onSelect={ratingField.onChange}
            onBlur={ratingField.onBlur}
            inputRef={ratingField.ref}
            legend={t('starLegend')}
            errorMessage={errors.rating?.message}
            getStarLabel={(rating) => t('starAriaLabel', { rating })}
          />

          {showCommentField && (
            <TextInput
              id={`${uid}-comment`}
              name="comment"
              register={register}
              errors={errors.comment}
              type="textarea"
              srLabel={t('commentLabel')}
              placeholder={tile['comment-placeholder']?.text ?? t('formPlaceholderComment')}
              inputWrapperClassName="min-h-28 items-start rounded-xl"
              // Replaces the textarea's default classes wholesale (TextInput spreads props after
              // its own className), so the base utilities are repeated here alongside resize-none.
              className="w-full resize-none placeholder:text-neutral-50 focus:outline-none"
              maxLength={STAR_RATING_MAX_COMMENT_LENGTH}
            />
          )}

          <Button
            type="submit"
            variant="secondary"
            disabled={isSubmitting}
            loading={isSubmitting}
            data-tile-element-id={buildTrackingId(trackingPrefix, 'submit')}
          >
            {tile['submit-button-text']?.text ?? t('formSubmitBtn')}
          </Button>

          {submissionError && (
            <p role="alert" className="text-sm text-red">
              {submissionError}
            </p>
          )}
        </form>
      </div>
    </Container>
  );
}
